import { ITax } from "@/types/db/product-category";
import { getProductCategoriesByPriceUuids } from "@/dexie/product-category";

interface ITaxWithPrice extends ITax {
  priceUuid: string;
}

/**
 * Fetches all taxes from product categories based on price IDs.
 * @param priceIds Array of price IDs to fetch taxes from.
 * @returns Array of unique taxes from the specified prices with their associated category IDs.
 */
export async function getTaxesFromPrices(
  priceUUIDs: string[]
): Promise<ITaxWithPrice[]> {
  if (priceUUIDs.length === 0) {
    return [];
  }

  // Fetch product categories that contain any of the given price UUIDs.
  const productCategories = await getProductCategoriesByPriceUuids(priceUUIDs);

  if (!productCategories || productCategories.length === 0) {
    return [];
  }

  const priceUuidSet = new Set(priceUUIDs);

  // Extract all taxes from the relevant prices.
  const allTaxes = productCategories.flatMap((pc) => {
    return pc.prices
      .filter((price) => priceUuidSet.has(price.uuid)) // Only consider prices we asked for
      .flatMap((price) =>
        (price.taxes || [])
          .filter((tax) => tax) // Remove null/undefined
          .map((tax) => ({
            ...tax,
            priceUuid: price.uuid,
          }))
      );
  });

  // Remove duplicates by tax ID, keeping the first categoryId encountered.
  const uniqueTaxes = Array.from(
    new Map(allTaxes.map((tax) => [tax.id, tax])).values()
  );

  return uniqueTaxes;
}
